import { useEffect, useState } from "react";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Bell, Mail, Zap, TrendingUp, Minus, Search as SearchIcon, Download } from "lucide-react";
import { getAlerts } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

const typeIcon = (type) => {
  switch (type) {
    case "new_competitor":
    case "New Ad": return <Zap className="w-3.5 h-3.5 text-primary" />;
    case "Removed": return <Minus className="w-3.5 h-3.5 text-gray-400" />;
    case "scaling_detected":
    case "Scaling": return <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />;
    case "trend_opportunity":
    case "New Trend": return <SearchIcon className="w-3.5 h-3.5 text-amber-500" />;
    default: return <Bell className="w-3.5 h-3.5 text-gray-400" />;
  }
};

const typeBadge = (type) => {
  const styles = {
    "new_competitor": "bg-primary/10 text-primary border-primary/25",
    "New Ad": "bg-primary/10 text-primary border-primary/25",
    "Removed": "bg-gray-50 text-gray-500 border-gray-200",
    "scaling_detected": "bg-emerald-50 text-emerald-600 border-emerald-200",
    "Scaling": "bg-emerald-50 text-emerald-600 border-emerald-200",
    "trend_opportunity": "bg-amber-50 text-amber-600 border-amber-200",
    "New Trend": "bg-amber-50 text-amber-600 border-amber-200",
  };
  return styles[type] || "bg-gray-50 text-gray-500 border-gray-200";
};

const formatAlertType = (type) => {
  const typeMap = {
    "new_competitor": "New Competitor",
    "scaling_detected": "Scaling",
    "trend_opportunity": "Opportunity"
  };
  return typeMap[type] || type;
};

export default function AlertsPage() {
  const [dailySummary, setDailySummary] = useState(true);
  const [instantAlerts, setInstantAlerts] = useState(true);
  const [monitoredBrands, setMonitoredBrands] = useState([]);
  const [monitoredKeywords, setMonitoredKeywords] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const shop = urlParams.get('shop');
        const response = await getAlerts(100, shop);
        if (!cancelled) {
          // Backend returns { success: true, data: { alerts: [...] } }
          const alerts = response.success ? response.data.alerts : [];
          setAlerts(alerts);
        }
      } catch (e) {
        if (!cancelled) setAlerts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="fade-in" data-testid="alerts-page">
      <div className="mb-8">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold tracking-tight text-gray-900 mb-1">Alerts</h1>
            <p className="text-sm text-gray-500">Configure notifications and view alert history.</p>
          </div>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 text-xs border border-gray-200 rounded-md px-3 py-1.5 bg-white hover:bg-gray-50 text-gray-700"
            onClick={() => window.open(`${BACKEND_URL}/alerts.csv`, "_blank")}
            data-testid="export-alerts-csv"
          >
            <Download className="w-3.5 h-3.5" />
            Export CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        {/* Toggles */}
        <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="alert-toggles">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Notification Settings</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <div className="text-sm text-gray-900">Daily Summary</div>
                  <div className="text-xs text-gray-400">Receive a daily digest of all activity</div>
                </div>
              </div>
              <Switch checked={dailySummary} onCheckedChange={setDailySummary} className="data-[state=checked]:bg-primary" data-testid="daily-summary-switch" />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                  <Zap className="w-4 h-4 text-amber-500" />
                </div>
                <div>
                  <div className="text-sm text-gray-900">Instant Alerts</div>
                  <div className="text-xs text-gray-400">Get notified immediately for new ads</div>
                </div>
              </div>
              <Switch checked={instantAlerts} onCheckedChange={setInstantAlerts} className="data-[state=checked]:bg-primary" data-testid="instant-alerts-switch" />
            </div>
          </div>
        </div>

        {/* Monitored Items */}
        <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="alert-monitors">
          <div className="mb-4">
            <div className="text-sm font-semibold text-gray-900 mb-2">Monitored Competitors</div>
            {monitoredBrands.length === 0 ? (
              <p className="text-xs text-gray-400">No competitors monitored yet.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {monitoredBrands.map((brand) => (
                  <span key={brand} className="bg-gray-50 text-gray-700 border border-gray-200 px-2.5 py-1 text-xs rounded-lg flex items-center gap-1.5 font-medium">
                    {brand}
                    <button onClick={() => setMonitoredBrands(monitoredBrands.filter((b) => b !== brand))} className="text-gray-400 hover:text-gray-600 transition-colors" data-testid={`remove-brand-${brand.toLowerCase().replace(/\s+/g, '-')}`}>
                      x
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="text-sm font-semibold text-gray-900 mb-2">Monitored Keywords</div>
            {monitoredKeywords.length === 0 ? (
              <p className="text-xs text-gray-400">No keywords monitored yet.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {monitoredKeywords.map((kw) => (
                  <span key={kw} className="bg-primary/10 text-gray-900 border border-primary/25 px-2.5 py-1 text-xs rounded-lg flex items-center gap-1.5 font-medium">
                    {kw}
                    <button onClick={() => setMonitoredKeywords(monitoredKeywords.filter((k) => k !== kw))} className="text-primary/50 hover:text-primary transition-colors" data-testid={`remove-keyword-${kw.toLowerCase().replace(/\s+/g, '-')}`}>
                      x
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Alert History */}
      <div data-testid="alert-history">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-gray-900">Alert History</h2>
          <span className="text-xs text-gray-400">{alerts.length} alerts</span>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-100 hover:bg-transparent bg-gray-50/50">
                <TableHead className="text-xs font-medium text-gray-500 h-9 px-4">Date</TableHead>
                <TableHead className="text-xs font-medium text-gray-500 h-9 px-4">Type</TableHead>
                <TableHead className="text-xs font-medium text-gray-500 h-9 px-4">Brand</TableHead>
                <TableHead className="text-xs font-medium text-gray-500 h-9 px-4">Platform</TableHead>
                <TableHead className="text-xs font-medium text-gray-500 h-9 px-4">Description</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {alerts.map((alert) => (
                <TableRow key={alert.id} className="border-gray-100 hover:bg-gray-50/50" data-testid={`alert-row-${alert.id}`}>
                  <TableCell className="px-4 py-3"><span className="text-xs text-gray-500 font-mono">{alert.createdAt ? new Date(alert.createdAt).toISOString().slice(0, 10) : 'N/A'}</span></TableCell>
                  <TableCell className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-medium rounded-md border ${typeBadge(alert.type)}`}>
                      {typeIcon(alert.type)}{alert.type}
                    </span>
                  </TableCell>
                  <TableCell className="px-4 py-3"><span className="text-sm text-gray-700">{alert.metadata?.competitor || 'N/A'}</span></TableCell>
                  <TableCell className="px-4 py-3"><span className="text-xs text-gray-500">{alert.metadata?.platform || 'Meta'}</span></TableCell>
                  <TableCell className="px-4 py-3"><span className="text-sm text-gray-500">{alert.message || alert.title}</span></TableCell>
                </TableRow>
              ))}
              {alerts.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={5} className="px-4 py-6 text-center">
                    <span className="text-sm text-gray-400">No alerts yet.</span>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
